import { useState, useEffect } from 'react';
import axios from 'axios';
import { CircleDot, ArrowDownCircle, ArrowUpCircle, Search, Plus } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NEW_OPTION = '__nuevo__';

const initialForm = {
    tipo: 'entrada',
    marca_select: '',
    marca_nueva: '',
    color_select: '',
    color_nuevo: '',
    cantidad: '',
    proveedor_id: '',
    observaciones: ''
};

export const Botones = () => {
    const [movimientos, setMovimientos] = useState([]);
    const [inventario, setInventario] = useState([]);
    const [marcas, setMarcas] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [movRes, invRes, marcasRes, provRes] = await Promise.all([
                axios.get(`${API_URL}/api/botones`),
                axios.get(`${API_URL}/api/botones/inventario`),
                axios.get(`${API_URL}/api/botones/marcas`),
                axios.get(`${API_URL}/api/proveedores`)
            ]);
            setMovimientos(movRes.data);
            setInventario(invRes.data);
            setMarcas(marcasRes.data);
            setProveedores(provRes.data);
        } catch (e) { toast.error('Error al cargar datos'); }
        finally { setLoading(false); }
    };

    // Compute final marca and color from form
    const getMarcaFinal = () => {
        if (form.marca_select === NEW_OPTION) return form.marca_nueva.trim();
        const m = marcas.find(mm => mm.marca === form.marca_select);
        return m ? m.marca_display : '';
    };
    const getColorFinal = () => {
        if (form.color_select === NEW_OPTION) return form.color_nuevo.trim();
        const m = marcas.find(mm => mm.marca === form.marca_select);
        const c = m?.colores.find(cc => cc.color === form.color_select);
        return c ? c.color_display : '';
    };

    // For SALIDA, derive marca/color from inventario (only items with stock > 0)
    const marcasConStock = inventario.filter(i => i.colores.some(c => c.stock > 0));
    const coloresDeMarcaSalida = (() => {
        const m = inventario.find(i => i.marca === form.marca_select);
        return m ? m.colores.filter(c => c.stock > 0) : [];
    })();

    const handleSubmit = async (e) => {
        e.preventDefault();
        const marca = getMarcaFinal() || (form.tipo === 'salida' ? (inventario.find(i => i.marca === form.marca_select)?.marca_display || '') : '');
        let color = getColorFinal();
        if (form.tipo === 'salida') {
            const m = inventario.find(i => i.marca === form.marca_select);
            const c = m?.colores.find(cc => cc.color === form.color_select);
            color = c ? c.color_display : '';
        }

        if (!marca || !color || !form.cantidad) {
            toast.error('Completa todos los campos requeridos'); return;
        }

        try {
            await axios.post(`${API_URL}/api/botones`, {
                tipo: form.tipo,
                tipo_boton: marca,
                color: color,
                cantidad: parseInt(form.cantidad),
                proveedor_id: form.proveedor_id || '',
                observaciones: form.observaciones || ''
            });
            toast.success(`${form.tipo === 'entrada' ? 'Entrada' : 'Salida'} registrada`);
            setModalOpen(false);
            setForm(initialForm);
            fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al registrar'); }
    };

    const openModal = (tipo) => { setForm({ ...initialForm, tipo }); setModalOpen(true); };

    const filtered = movimientos.filter(m =>
        (m.tipo_boton || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.color || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="botones-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100">Inventario de Botones</h1>
                    <p className="text-zinc-400 mt-1">Control por marca y color</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => openModal('entrada')} className="bg-emerald-500 hover:bg-emerald-600" data-testid="btn-entrada-boton">
                        <ArrowDownCircle className="w-5 h-5 mr-2" /> Entrada
                    </Button>
                    <Button onClick={() => openModal('salida')} className="bg-red-500 hover:bg-red-600" data-testid="btn-salida-boton">
                        <ArrowUpCircle className="w-5 h-5 mr-2" /> Salida
                    </Button>
                </div>
            </div>

            {/* Inventory Cards (grouped by marca, breakdown by color) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {inventario.length > 0 ? inventario.map((item) => (
                    <Card key={item.marca} className="card" data-testid={`card-marca-${item.marca}`}>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <CircleDot className="w-5 h-5 text-amber-400" />
                                <p className="text-base font-semibold text-zinc-100 capitalize">{item.marca_display}</p>
                            </div>
                            <p className="text-3xl font-bold text-amber-400 mb-3" data-testid={`stock-marca-${item.marca}`}>
                                {item.total} <span className="text-xs font-normal text-zinc-500">total</span>
                            </p>
                            <div className="space-y-1.5 border-t border-zinc-700/60 pt-2">
                                {item.colores.filter(c => c.stock !== 0).map((c) => (
                                    <div key={c.color} className="flex justify-between items-center text-sm">
                                        <span className="text-zinc-400 capitalize">{c.color_display}</span>
                                        <span className={`font-semibold ${c.stock > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{c.stock}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )) : (
                    <Card className="card col-span-full"><CardContent className="p-8 text-center text-zinc-400">Sin inventario de botones aun. Registra una entrada para empezar.</CardContent></Card>
                )}
            </div>

            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input placeholder="Buscar por marca o color..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
            </div>

            <Card className="card"><CardContent className="p-0"><div className="overflow-x-auto">
                <Table><TableHeader><TableRow className="bg-zinc-800">
                    <TableHead className="table-header">Fecha</TableHead>
                    <TableHead className="table-header">Tipo Mov.</TableHead>
                    <TableHead className="table-header">Marca</TableHead>
                    <TableHead className="table-header">Color</TableHead>
                    <TableHead className="table-header">Cantidad</TableHead>
                    <TableHead className="table-header">Usuario</TableHead>
                </TableRow></TableHeader><TableBody>
                    {filtered.length > 0 ? filtered.map(m => (
                        <TableRow key={m.id} className="table-row">
                            <TableCell className="table-cell">{m.created_at?.split('T')[0]}</TableCell>
                            <TableCell className="table-cell">
                                <span className={m.tipo === 'entrada' ? 'badge-success' : 'badge-danger'}>{m.tipo === 'entrada' ? 'Entrada' : 'Salida'}</span>
                            </TableCell>
                            <TableCell className="table-cell font-medium capitalize">{m.tipo_boton}</TableCell>
                            <TableCell className="table-cell capitalize">{m.color}</TableCell>
                            <TableCell className="table-cell">
                                <span className={m.tipo === 'entrada' ? 'text-emerald-400' : 'text-red-400'}>{m.tipo === 'entrada' ? '+' : '-'}{m.cantidad}</span>
                            </TableCell>
                            <TableCell className="table-cell">{m.usuario_nombre || '-'}</TableCell>
                        </TableRow>
                    )) : (
                        <TableRow><TableCell colSpan={6} className="text-center py-8 text-zinc-400">Sin movimientos</TableCell></TableRow>
                    )}
                </TableBody></Table>
            </div></CardContent></Card>

            {/* Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="modal-boton">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {form.tipo === 'entrada' ? <ArrowDownCircle className="w-5 h-5 text-emerald-400" /> : <ArrowUpCircle className="w-5 h-5 text-red-400" />}
                            {form.tipo === 'entrada' ? 'Entrada' : 'Salida'} de Botones
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* MARCA */}
                        <div className="form-group">
                            <Label className="form-label">Marca *</Label>
                            {form.tipo === 'entrada' ? (
                                <>
                                    <Select value={form.marca_select} onValueChange={v => setForm({ ...form, marca_select: v, color_select: '', color_nuevo: '' })}>
                                        <SelectTrigger data-testid="select-marca-boton"><SelectValue placeholder="Seleccionar marca" /></SelectTrigger>
                                        <SelectContent>
                                            {marcas.map(m => <SelectItem key={m.marca} value={m.marca}>{m.marca_display}</SelectItem>)}
                                            <SelectItem value={NEW_OPTION}>➕ Nueva marca</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {form.marca_select === NEW_OPTION && (
                                        <Input
                                            className="mt-2"
                                            value={form.marca_nueva}
                                            onChange={e => setForm({ ...form, marca_nueva: e.target.value })}
                                            placeholder="Nombre de la nueva marca"
                                            data-testid="input-marca-nueva"
                                        />
                                    )}
                                </>
                            ) : (
                                <Select value={form.marca_select} onValueChange={v => setForm({ ...form, marca_select: v, color_select: '' })}>
                                    <SelectTrigger data-testid="select-marca-boton-salida"><SelectValue placeholder="Seleccionar marca con stock" /></SelectTrigger>
                                    <SelectContent>
                                        {marcasConStock.length > 0 ? marcasConStock.map(m => (
                                            <SelectItem key={m.marca} value={m.marca}>{m.marca_display} ({m.total} total)</SelectItem>
                                        )) : (
                                            <div className="px-2 py-1.5 text-xs text-zinc-500">Sin stock disponible</div>
                                        )}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>

                        {/* COLOR */}
                        <div className="form-group">
                            <Label className="form-label">Color *</Label>
                            {form.tipo === 'entrada' ? (
                                <>
                                    <Select
                                        value={form.color_select}
                                        onValueChange={v => setForm({ ...form, color_select: v })}
                                        disabled={!form.marca_select}
                                    >
                                        <SelectTrigger data-testid="select-color-boton">
                                            <SelectValue placeholder={form.marca_select ? "Seleccionar color" : "Primero selecciona marca"} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {(marcas.find(m => m.marca === form.marca_select)?.colores || []).map(c => (
                                                <SelectItem key={c.color} value={c.color}>{c.color_display}</SelectItem>
                                            ))}
                                            <SelectItem value={NEW_OPTION}>➕ Nuevo color</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {form.color_select === NEW_OPTION && (
                                        <Input
                                            className="mt-2"
                                            value={form.color_nuevo}
                                            onChange={e => setForm({ ...form, color_nuevo: e.target.value })}
                                            placeholder="Nombre del nuevo color"
                                            data-testid="input-color-nuevo"
                                        />
                                    )}
                                </>
                            ) : (
                                <Select
                                    value={form.color_select}
                                    onValueChange={v => setForm({ ...form, color_select: v })}
                                    disabled={!form.marca_select}
                                >
                                    <SelectTrigger data-testid="select-color-boton-salida">
                                        <SelectValue placeholder={form.marca_select ? "Seleccionar color con stock" : "Primero selecciona marca"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {coloresDeMarcaSalida.length > 0 ? coloresDeMarcaSalida.map(c => (
                                            <SelectItem key={c.color} value={c.color}>{c.color_display} ({c.stock} disponibles)</SelectItem>
                                        )) : (
                                            <div className="px-2 py-1.5 text-xs text-zinc-500">Sin stock disponible</div>
                                        )}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>

                        {/* CANTIDAD */}
                        <div className="form-group">
                            <Label className="form-label">Cantidad *</Label>
                            <Input
                                type="number"
                                min="1"
                                value={form.cantidad}
                                onChange={e => setForm({ ...form, cantidad: e.target.value })}
                                data-testid="input-cantidad-boton"
                            />
                        </div>

                        {form.tipo === 'entrada' && (
                            <div className="form-group">
                                <Label className="form-label">Proveedor</Label>
                                <Select value={form.proveedor_id} onValueChange={v => setForm({ ...form, proveedor_id: v })}>
                                    <SelectTrigger><SelectValue placeholder="Seleccionar (opcional)" /></SelectTrigger>
                                    <SelectContent>{proveedores.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="form-group">
                            <Label className="form-label">Observaciones</Label>
                            <Input value={form.observaciones} onChange={e => setForm({ ...form, observaciones: e.target.value })} placeholder="Notas (opcional)" />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
                            <Button type="submit" className={form.tipo === 'entrada' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'} data-testid="submit-boton">
                                Registrar {form.tipo === 'entrada' ? 'Entrada' : 'Salida'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
